import { useEmergencyCallData } from '../hooks/useEmergencyCallData'
import { useToasts } from '@/hooks/useToasts'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiExclamation } from 'react-icons/hi'
import { MdInfo } from 'react-icons/md'

import { AlertDialogBody, AlertDialogFooter, Box, Flex } from '@chakra-ui/react'

import {
  BasicAlertDialog,
  Button,
  FacilityOptionLabel,
  FormInputSelectControl,
} from '@/components/ui'
import { Colors } from '@/styles/colors'
import {
  OptionI,
  createFacilityAddressFilter,
} from '@/utils/forms/optionBuilders'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'

import { ModalAlert } from './ModalAlert'

interface ConfirmEmergencyCallDialogIProps {
  isOpen: boolean
  onClose: () => void
  emergencyCallCategoryId: string | null
  handleOnClick: (
    facilityId?: string,
    facilityName?: string,
    incidentId?: string,
    incidentDisplayId?: string,
    emergencyNumber?: string
  ) => void
}

interface FormInputsIProps {
  facility: OptionI
}

export const GlobalEmergencyCallModal = ({
  isOpen,
  onClose,
  handleOnClick,
  emergencyCallCategoryId,
}: ConfirmEmergencyCallDialogIProps) => {
  const { t } = useTranslation()
  const { showError } = useToasts()
  const form = useForm<FormInputsIProps>({
    mode: 'onChange',
  })
  const { control, watch } = form
  const watchedFacility = watch('facility')

  const {
    facilityOptions,
    isCreatingIncident,
    isLoadingFacilities,
    isFacilitiesError,
    isLoadingFacilityDetails,
    emergencyNumber: hookEmergencyNumber,
    isEmergencyCallingConfigured,
    handleCreateIncidentAndCall,
    setFacilityName,
  } = useEmergencyCallData({
    facility: watchedFacility,
    emergencyCallCategoryId,
    handleOnClick,
    onClose,
  })

  const handlePlaceCallClick = async () => {
    if (!watchedFacility?.value) {
      showError('Please select a facility before placing the emergency call.')
      return
    }

    await handleCreateIncidentAndCall(watchedFacility.value)
  }

  const canNotMakeEmergencyCall =
    watchedFacility &&
    !isLoadingFacilityDetails &&
    !isEmergencyCallingConfigured

  const canProceed = watchedFacility?.value && isEmergencyCallingConfigured

  return (
    <BasicAlertDialog
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size='xl'
      title='Emergency Call'
    >
      <AlertDialogBody>
        {!watchedFacility ? (
          <Box mb='4' mt='-6'>
            Please select a facility to make an emergency call
          </Box>
        ) : (
          isEmergencyCallingConfigured && (
            <ModalAlert
              icon={<HiExclamation size='32' />}
              label={`This will call ${watchedFacility?.label} local emergency number: ${hookEmergencyNumber}`}
              mt='-4'
              status='warning'
              title='Warning:'
            />
          )
        )}
        {canNotMakeEmergencyCall && (
          <ModalAlert
            boldedText={watchedFacility?.label}
            icon={<HiExclamation size='32' />}
            label='Emergency calls are not configured for '
            mt='-4'
            title='Warning:'
          />
        )}

        <FormInputSelectControl
          control={control}
          dataTestId='globalEmergencyCallModal_facilityName'
          defaultValue={null}
          // Filters by facility name, short name, and address.
          // Without filterOption, React Select defaults to label-only search,
          // which would hide address/short-name-only matches the server
          // already returned.
          filterOption={createFacilityAddressFilter}
          formatOptionLabel={(option, meta) => (
            <FacilityOptionLabel
              isValueContext={meta?.context === 'value'}
              option={option}
            />
          )}
          id='facility'
          isClearable
          isDisabled={false}
          isLoading={isLoadingFacilities}
          isSearchable
          label='Facility'
          labelColour={Colors.formLabel}
          labelOpacity={0.5}
          noOptionsMessage={({ inputValue }) =>
            getFacilityNoOptionsMessage(
              inputValue,
              isFacilitiesError,
              t('common:facility_search_error')
            )
          }
          onInputChange={setFacilityName}
          options={facilityOptions}
          placeholder='Select a facility'
          rules={{ required: false }}
        />

        {canNotMakeEmergencyCall && (
          <ModalAlert
            color='blue.600'
            icon={<MdInfo size='32' />}
            label="Contact the facility's designated emergency number if available, or reach out to your HiveWatch representative to enable this feature"
            status='info'
            title='Need help?'
          />
        )}
      </AlertDialogBody>
      <AlertDialogFooter>
        <Button mr='3' onClick={onClose} variant='secondary'>
          Cancel
        </Button>
        <Flex color='red'>
          <Button
            disabled={!canProceed || isCreatingIncident}
            isLoading={isCreatingIncident}
            loadingText='Placing call...'
            onClick={handlePlaceCallClick}
            variant='danger'
          >
            Place Call
          </Button>
        </Flex>
      </AlertDialogFooter>
    </BasicAlertDialog>
  )
}
